<script lang="ts">
	import { create } from "@bufbuild/protobuf";
	import { timestampDate, timestampFromDate } from "@bufbuild/protobuf/wkt";
	import { Helper } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { WindowSchema, type Poll } from "#gen/forms/v1/poll_pb.js";
	import type { User } from "#gen/identities/v1/identities_pb.js";
	import Toggle from "#lib/components/Toggle.svelte";
	import DateTimeInput from "#lib/components/calendar/DateTimeInput.svelte";
	import { DEFAULT_DATE_FORMAT_OPTIONS, DEFAULT_DATE_PLACEHOLDER } from "#lib/components/page/components/poll/constants.js";
	import { isEndBeforeOrAtStart } from "#lib/components/page/components/poll/utils.js";
	import type { Writable } from "svelte/store";

	interface Props {
		poll: Poll;
		onChange?: (poll: Poll) => void;
	}

	const user: Writable<User> = getContext("user");

	let { poll, onChange }: Props = $props();

	let showEndBeforeStartError = $state(false);

	const inputColor = $derived(showEndBeforeStartError ? "red" : "default");

	function handleResultsWindowChange(date: Date, type: "start" | "end") {
		const newWindow = create(WindowSchema, { ...poll.postVoteResultsWindow });
		if (type === "start") {
			newWindow.start = timestampFromDate(date);
		} else {
			newWindow.end = timestampFromDate(date);
		}
		if (isEndBeforeOrAtStart(newWindow.start, newWindow.end)) {
			showEndBeforeStartError = true;
			return;
		}
		showEndBeforeStartError = false;
		onChange?.({ ...poll, postVoteResultsWindow: newWindow });
	}

	function handleEndToggle(event: Event) {
		const target = event.target as HTMLInputElement;
		showEndBeforeStartError = false;
		if (target.checked) {
			onChange?.({ ...poll, postVoteResultsWindow: undefined });
		} else {
			onChange?.({ ...poll, postVoteResultsWindow: create(WindowSchema, { ...poll.postVoteResultsWindow, end: timestampFromDate(new Date()) }) });
		}
	}
</script>

<form class="flex w-full flex-col gap-4" onsubmit={e => e.preventDefault()}>
	<div class="flex w-full flex-col gap-4 rounded-lg border p-4">
		<Helper>Controls when final results are shown after voting closes. Leave start empty to show results immediately when voting ends.</Helper>
		<div class="flex w-full flex-col gap-2 rounded-lg border p-4">
			<div class="grid grid-cols-2 gap-4">
				<DateTimeInput
					dateLabel="Results start (optional)"
					allDay={true}
					datePlaceholder={DEFAULT_DATE_PLACEHOLDER}
					dateFormatOptions={DEFAULT_DATE_FORMAT_OPTIONS}
					timezone={poll.timezone || $user.timezone}
					timestamp={poll.postVoteResultsWindow?.start ? timestampDate(poll.postVoteResultsWindow.start) : undefined}
					onChange={date => handleResultsWindowChange(date, "start")}
					disabled={!poll.postVoteResultsWindow}
					color={inputColor}
				/>
				<DateTimeInput
					dateLabel="Results end"
					allDay={true}
					datePlaceholder={DEFAULT_DATE_PLACEHOLDER}
					dateFormatOptions={DEFAULT_DATE_FORMAT_OPTIONS}
					timezone={poll.timezone || $user.timezone}
					timestamp={poll.postVoteResultsWindow?.end ? timestampDate(poll.postVoteResultsWindow.end) : undefined}
					onChange={date => handleResultsWindowChange(date, "end")}
					disabled={!poll.postVoteResultsWindow}
					color={inputColor}
				/>
			</div>
			{#if showEndBeforeStartError}
				<Helper color="red" role="alert">End date must be later than start date</Helper>
			{/if}
		</div>
		<Toggle class="w-fit" checked={!poll.postVoteResultsWindow} on:change={e => handleEndToggle(e)}>Show results indefinitely</Toggle>
	</div>
</form>
